import { RsrActionModal } from '../components/RsrActionModal';
import { useRef, useState } from 'react';

import { CalendarClock } from 'lucide-react';

import type { RsrCard as Card, RsrState } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useCandidateDepartments, useCountries } from '@/features/candidates/api';
import { RSR_LABEL } from '@/features/candidates/labels';
import { useDateFormat } from '@/features/company/api';
import { cx } from '@/lib/cx';
import { useDebounced } from '@/lib/useDebounced';

import { canDropOnRsr, useAdvanceRsr, useRsrBoard, type BoardFilters } from '../api';
import { RsrCard } from '../components/RsrCard';
import styles from './RsrBoardPage.module.css';

/**
 * The RSR screening board.
 *
 * Three of its seven columns are read off the booking date rather than set by anybody:
 * a screening with a date ahead of it is Arranged, one whose date has been and gone has
 * Happened, and one with no date at all is Pending. Nothing runs to make that true:
 * the column is worked out every time the board is asked for, so a booking moves across
 * on its own the morning after it lapses.
 */
export function RsrBoardPage() {
  const dateFormat = useDateFormat();

  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [destination, setDestination] = useState('');
  const debouncedSearch = useDebounced(search, 300);

  const filters: BoardFilters = {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(department ? { department_id: department } : {}),
    ...(destination ? { preferred_country_id: destination } : {}),
  };

  const board = useRsrBoard(filters);
  const advance = useAdvanceRsr(filters);
  const departments = useCandidateDepartments();
  const countries = useCountries();

  const [action, setAction] = useState<{ card: Card; type: 'arranged' | 'account' } | null>(
    null,
  );
  const [refused, setRefused] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [dragged, setDragged] = useState<Card | null>(null);
  const [over, setOver] = useState<RsrState | null>(null);

  // Read synchronously by `dragover`, which has to decide whether to accept the drop
  // before React has necessarily re-rendered. See the Flyst board for the full note.
  const draggedRef = useRef<Card | null>(null);
  const startDrag = (card: Card | null) => {
    draggedRef.current = card;
    setDragged(card);
  };

  const canDrop = (state: RsrState) =>
    draggedRef.current !== null &&
    draggedRef.current.rsr_state !== state &&
    canDropOnRsr(state);

  const move = (card: Card, state: RsrState) => {
    setRefused(null);
    if (state === 'arranged') {
      setAction({ card, type: 'arranged' });
      return;
    }
    setMoving(card.id);
    advance.mutate(
      { id: card.id, state },
      {
        onError: (error) => {
          if (error instanceof ApiError && error.code === 'ATS_ACCOUNT_REQUIRED') {
            setAction({ card, type: 'account' });
            return;
          }
          setRefused(
            error instanceof ApiError
              ? `${card.full_name}: ${error.message}`
              : `${card.full_name} could not be moved.`,
          );
        },
        onSettled: () => {
          setMoving(null);
        },
      },
    );
  };

  return (
    <Page>
      <PageHeader
        title="RSR screening"
        description="The sponsor's screening, from the booking through to the outcome. Pending, Arranged and Happened follow the date on their own."
      />

      <Panel>
        <div className={styles.filters}>
          <Input
            type="search"
            placeholder="Search name, email, phone or reference"
            aria-label="Search candidates"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          <Select
            aria-label="Department"
            value={department}
            onChange={(event) => {
              setDepartment(event.target.value);
            }}
            placeholder="All departments"
            options={(departments.data ?? []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
          <Select
            aria-label="Destination"
            value={destination}
            onChange={(event) => {
              setDestination(event.target.value);
            }}
            placeholder="All destinations"
            options={(countries.data ?? []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
          <span className={styles.count}>
            {board.data ? (
              <>
                <b>{board.data.total}</b> {board.data.total === 1 ? 'candidate' : 'candidates'}{' '}
                on this board
              </>
            ) : null}
          </span>
        </div>
      </Panel>

      {refused ? (
        <Alert tone="danger">
          <b>That move was not allowed.</b> {refused}
        </Alert>
      ) : null}

      {board.isPending ? (
        <div className={styles.loading}>
          <Spinner size={20} label="Loading the board" />
        </div>
      ) : board.isError ? (
        <ErrorState
          message="The screening board did not load."
          onRetry={() => {
            void board.refetch();
          }}
        />
      ) : board.data.total === 0 ? (
        <EmptyState
          icon={<CalendarClock size={20} />}
          title="Nobody has been handed over yet"
          body={
            search || department || destination
              ? 'No candidate matches these filters.'
              : 'Candidates arrive here once their Flyst screening reaches Sponsor screening arranged, which is where the date is booked.'
          }
        />
      ) : (
        <div className={styles.board}>
          {board.data.columns.map((column) => (
            <section
              key={column.state}
              className={cx(styles.column, over === column.state && styles.columnOver)}
              aria-label={RSR_LABEL[column.state]}
              onDragOver={(event) => {
                if (!canDrop(column.state)) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                setOver(column.state);
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setOver(null);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setOver(null);
                const carried = draggedRef.current;
                if (carried && canDrop(column.state)) move(carried, column.state);
                startDrag(null);
              }}
            >
              <header className={styles.columnHead}>
                <h2 className={styles.columnTitle}>{RSR_LABEL[column.state]}</h2>
                <span className={styles.columnCount}>{column.total}</span>
              </header>

              <div className={styles.cards}>
                {column.cards.length === 0 ? (
                  <p className={styles.empty}>
                    {/* The plan asks for this one column to explain itself: an empty
                        Pending reads as "nothing to do" when it usually means nobody
                        has been handed over yet. */}
                    {column.state === 'pending'
                      ? 'Candidates arrive once their Flyst screening is handed over'
                      : 'Nothing here'}
                  </p>
                ) : (
                  column.cards.map((card) => (
                    <RsrCard
                      key={card.id}
                      card={card}
                      dateFormat={dateFormat}
                      moving={moving === card.id}
                      dragging={dragged?.id === card.id}
                      onOpen={() => {
                        setAction({ card, type: 'arranged' });
                      }}
                      onMove={move}
                      onDragStart={startDrag}
                      onDragEnd={() => {
                        startDrag(null);
                        setOver(null);
                      }}
                    />
                  ))
                )}

                {column.total > column.cards.length ? (
                  <p className={styles.more}>
                    {column.total - column.cards.length} more. Narrow the filters to see them.
                  </p>
                ) : null}
              </div>
            </section>
          ))}
        </div>
      )}
      {action ? (
        <RsrActionModal
          card={action.card}
          action={action.type}
          onClose={() => {
            setAction(null);
          }}
          onComplete={async (state, scheduledOn) => {
            await advance.mutateAsync({
              id: action.card.id,
              state,
              ...(scheduledOn ? { scheduledOn } : {}),
            });
          }}
        />
      ) : null}
    </Page>
  );
}
